"use client";

import { c as _c } from "react/compiler-runtime";
import React, { Fragment, useEffect } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { useConfettiContext } from "@/contexts/ConfettiContextProvider";
import { useAccountContext } from "../AccountIdLayoutContext";
import { cn, getPlatformClass } from "@/lib/utils";
import { useAccountPlatform } from "@/hooks/useAccountPlatforms";
import Card from "@/components/presentational/tailwind/Card";
import LiveActivityFeed from "@/components/presentational/tailwind/LiveActivityFeed";
import FollowersChart from "@/components/presentational/tailwind/FollowersChart/FollowersChart";
import FollowersMapChart from "@/components/presentational/tailwind/Map/FollowersMapChart";
import { RadioIcon } from "@/components/ui/radio";
import { MobileTooltip, MobileTooltipContent, MobileTooltipTrigger } from "@/components/ui/mobile-tooltip";
import { ColorType } from "@/components/presentational/tailwind/BarChartList";
const BarChartList = dynamic(() => import("@/components/presentational/tailwind/BarChartList"));
import SetupCompletionCard from "@/components/presentational/tailwind/SetupCompletionCard";
import TrialBlurGate from "@/components/presentational/tailwind/TrialBlurGate";
import { isTrialExpired } from "@/utils/account-type";
import { useUser } from "@/contexts/AuthContext";
import GrowthReportBanner from "@/components/outfame/GrowthReportBanner";
export default function AccountPage() {
  const $ = _c(47);
  const {
    account
  } = useAccountContext();
  const {
    setShowConfetti
  } = useConfettiContext();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("upgraded");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const justUpgraded = t0;
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams.get("connected");
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const justConnected = t1;
  let t2;
  if ($[4] !== searchParams) {
    t2 = searchParams.get("connect-tt");
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const showConnectTiktokModal = t2;
  const {
    setIsConnectAccountModalOpened,
    setConnectAccountModalInitialPlatform,
    openUpgradeModal
  } = useModalContext();
  const platform = useAccountPlatform();
  const user = useUser();
  let t3;
  if ($[6] !== account?.id || $[7] !== justConnected || $[8] !== justUpgraded || $[9] !== openUpgradeModal || $[10] !== setConnectAccountModalInitialPlatform || $[11] !== setIsConnectAccountModalOpened || $[12] !== setShowConfetti || $[13] !== showConnectTiktokModal) {
    t3 = () => {
      if (justUpgraded || justConnected && justConnected === "true") {
        setShowConfetti(true);
        if (!justUpgraded) {
          openUpgradeModal(account?.id);
        }
        setTimeout(() => {
          setShowConfetti(false);
        }, 3000);
      }
      if (showConnectTiktokModal) {
        setConnectAccountModalInitialPlatform("tiktok");
        setIsConnectAccountModalOpened(true);
      }
    };
    $[6] = account?.id;
    $[7] = justConnected;
    $[8] = justUpgraded;
    $[9] = openUpgradeModal;
    $[10] = setConnectAccountModalInitialPlatform;
    $[11] = setIsConnectAccountModalOpened;
    $[12] = setShowConfetti;
    $[13] = showConnectTiktokModal;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== justConnected || $[16] !== justUpgraded || $[17] !== openUpgradeModal || $[18] !== setConnectAccountModalInitialPlatform || $[19] !== setIsConnectAccountModalOpened || $[20] !== setShowConfetti || $[21] !== showConnectTiktokModal) {
    t4 = [justUpgraded, justConnected, showConnectTiktokModal, setShowConfetti, openUpgradeModal, setIsConnectAccountModalOpened, setConnectAccountModalInitialPlatform];
    $[15] = justConnected;
    $[16] = justUpgraded;
    $[17] = openUpgradeModal;
    $[18] = setConnectAccountModalInitialPlatform;
    $[19] = setIsConnectAccountModalOpened;
    $[20] = setShowConfetti;
    $[21] = showConnectTiktokModal;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  useEffect(t3, t4);
  if (!account) {
    return null;
  }
  const isTrial = account?.account_type === "trial";
  let t5;
  if ($[23] !== account) {
    t5 = isTrialExpired(account);
    $[23] = account;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const trialExpired = t5;
  let t6;
  if ($[25] !== account.id || $[26] !== openUpgradeModal || $[27] !== trialExpired) {
    t6 = trialExpired && <div className="mb-6 rounded-xl bg-red-500 px-4 py-3 text-center shadow-lg shadow-red-500/20"><p className="text-[15px] font-medium text-white">Your trial has expired{" "}<span className="mx-1 text-white/60">·</span>{" "}<button onClick={() => openUpgradeModal(account.id)} className="font-bold text-white underline decoration-white/50 underline-offset-2 transition-all hover:decoration-white">Upgrade now</button>{" "}to start growing</p></div>;
    $[25] = account.id;
    $[26] = openUpgradeModal;
    $[27] = trialExpired;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== account || $[30] !== trialExpired) {
    t7 = account && !trialExpired ? <SetupCompletionCard accountId={account.id} accountData={account} className="mb-6" /> : null;
    $[29] = account;
    $[30] = trialExpired;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== account.active || $[33] !== trialExpired || $[34] !== user?.id) {
    t8 = account?.active && !trialExpired && user?.id && <GrowthReportBanner className="mb-6" />;
    $[32] = account.active;
    $[33] = trialExpired;
    $[34] = user?.id;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== account || $[37] !== isTrial || $[38] !== openUpgradeModal || $[39] !== platform || $[40] !== trialExpired) {
    t9 = trialExpired ? <div className="relative"><div className="pointer-events-none select-none blur-md opacity-50"><div className="flex flex-col gap-6"><div className="grid grid-cols-1 gap-6 xl:grid-cols-3 xl:items-start"><Card className="xl:col-span-2" noPadding={true}><FollowersChart account={account} /></Card><Card title="Live Feed" icon={<RadioIcon size={18} continuous={true} className="text-red-500" />} noPadding={true}><div className="px-4 py-3"><LiveActivityFeed account={account} /></div></Card></div><Card className="relative" title="New Followers Location"><FollowersMapChart mapData={(account.analytics as any)?.top_5_countries} isEmpty={!(account.analytics && typeof account.analytics === "object" && "top_5_countries" in account.analytics && Object.keys((account.analytics as any).top_5_countries).length > 0)} isActive={account.active} /></Card><div className="grid grid-cols-1 gap-6 sm:grid-cols-2"><Card className="col-span-1" title="Top Traffic Sources"><BarChartList metric="top_5_traffic_sources" color={getPlatformClass(platform, "fuchsia", "cyan") as ColorType} isEmpty={true} /></Card><Card className="col-span-1" title="Top Followers Interests"><BarChartList metric="top_5_interests" color={getPlatformClass(platform, "orange", "rose") as ColorType} isEmpty={true} /></Card></div></div></div><div className="pointer-events-none absolute inset-0"><div className="pointer-events-auto sticky top-[40vh] z-10 mx-auto flex w-fit flex-col items-center rounded-2xl bg-white/95 px-8 py-10 text-center shadow-2xl shadow-gray-900/10 backdrop-blur-sm"><div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50"><svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg></div><h3 className="text-xl font-bold text-gray-900">Your trial has expired</h3><p className="mt-2 max-w-[280px] text-[15px] text-gray-500">Upgrade now to unlock your dashboard and continue growing your account.</p><button onClick={() => openUpgradeModal(account.id)} className={cn("mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3", "bg-[rgb(255,163,209)] text-[15px] font-bold text-gray-900", "transition-all duration-200", "hover:bg-[#fc83bf] hover:shadow-lg hover:shadow-pink-500/20", "focus:outline-none focus:ring-2 focus:ring-pink-200", "active:scale-[0.98]")}><svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>Upgrade now</button></div></div></div> : <div className="flex flex-col gap-6"><div className="grid grid-cols-1 gap-6 xl:grid-cols-3 xl:items-start"><Card className="xl:col-span-2" noPadding={true}><FollowersChart account={account} /></Card><Card title="Live Feed" icon={<RadioIcon size={18} continuous={true} className="text-red-500" />} noPadding={true} rightSideWrapper={<MobileTooltip><MobileTooltipTrigger asChild={true}><div className="h-2 w-2 rounded-full bg-white ring-2 ring-red-500/60"><div className="h-2 w-2 animate-ping rounded-full bg-red-500" /></div></MobileTooltipTrigger><MobileTooltipContent side="left" className="max-w-[300px]">This is a live feed of your account's growth activity and follower acquisition efforts in real-time.</MobileTooltipContent></MobileTooltip>}><div className="px-4 py-3"><LiveActivityFeed account={account} /></div></Card></div><Card className="relative" title="New Followers Location" tooltip="This is a visual representation of the new followers location over the last 30 days."><TrialBlurGate isTrial={isTrial} accountId={account.id} ctaText="Unlock location insights" description="See where your new followers are coming from" minHeight="240px"><FollowersMapChart mapData={(account.analytics as any)?.top_5_countries} isEmpty={!(account.analytics && typeof account.analytics === "object" && "top_5_countries" in account.analytics && Object.keys((account.analytics as any).top_5_countries).length > 0)} isActive={account.active} /></TrialBlurGate></Card><div className="grid grid-cols-1 gap-6 sm:grid-cols-2"><Card className="col-span-1" title="Top Traffic Sources" tooltip="This is a list of the top traffic sources for the last 30 days."><TrialBlurGate isTrial={isTrial} accountId={account.id} ctaText="Unlock traffic sources" description="See how followers discover you" minHeight="200px"><BarChartList metric="top_5_traffic_sources" color={getPlatformClass(platform, "fuchsia", "cyan") as ColorType} isEmpty={!(account.analytics && typeof account.analytics === "object" && "top_5_traffic_sources" in account.analytics && Object.keys(account.analytics.top_5_traffic_sources).length > 0)} /></TrialBlurGate></Card><Card className="col-span-1" title="Top Followers Interests" tooltip="This is a list of the top followers interests for the last 30 days."><TrialBlurGate isTrial={isTrial} accountId={account.id} ctaText="Unlock audience insights" description="See what your followers care about" minHeight="200px"><BarChartList metric="top_5_interests" color={getPlatformClass(platform, "orange", "rose") as ColorType} isEmpty={!(account.analytics && typeof account.analytics === "object" && "top_5_interests" in account.analytics && Object.keys(account.analytics.top_5_interests).length > 0)} /></TrialBlurGate></Card></div></div>;
    $[36] = account;
    $[37] = isTrial;
    $[38] = openUpgradeModal;
    $[39] = platform;
    $[40] = trialExpired;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t10 = <Fragment>{t6}{t7}{t8}{t9}</Fragment>;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  return t10;
}